/**
 * Red del servidor (variante tarjetas).
 *
 * Aquí se juntan las tres caras de la red que antes estaban repartidas:
 *   · tarjetas de red (componentes NIC, duplicadas desde el inventario),
 *   · teams / bondings detectados por el agente,
 *   · IPs (las pinta `ElementIpPanel`, que se monta debajo en la pestaña).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../services/api';

function nombreNic(comp) {
  return [comp.manufacturer, comp.model].filter(Boolean).join(' ') || `NIC #${comp.id}`;
}

export default function ServerCardsNetwork({
  serverId,
  companyId,
  teams = [],
  onOpenInventory,
  onCountChange,
}) {
  const [nics, setNics] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');

  const cargar = useCallback(async () => {
    if (!serverId || !companyId) return;
    setCargando(true);
    setError('');
    try {
      const res = await api.getComponents(serverId, companyId);
      const lista = (Array.isArray(res?.data) ? res.data : [])
        .filter((c) => c.component_type === 'nic');
      setNics(lista);
      onCountChange?.(lista.length);
    } catch (e) {
      setError(e.message || 'No se pudieron cargar las tarjetas de red');
    } finally {
      setCargando(false);
    }
  }, [serverId, companyId, onCountChange]);

  useEffect(() => { cargar(); }, [cargar]);

  const totalPuertos = useMemo(
    () => nics.reduce((acc, nic) => acc + (Array.isArray(nic.ports) ? nic.ports.length : 0), 0),
    [nics],
  );

  if (cargando) {
    return <div className="sf2-card"><div className="sf2-empty">Cargando tarjetas de red…</div></div>;
  }

  return (
    <div className="sf2-summary">
      <div className="sf2-card">
        <h3>
          Tarjetas de red y equipos
          {onOpenInventory && (
            <button type="button" className="sf2-link" onClick={onOpenInventory}>Editar componentes</button>
          )}
        </h3>
        <div className="sf2-hl">
          <div className="sf2-kpi"><span>Tarjetas NIC</span><b>{nics.length}</b></div>
          <div className="sf2-kpi"><span>Puertos</span><b>{totalPuertos}</b></div>
          <div className="sf2-kpi"><span>Teams</span><b>{teams.length}</b></div>
        </div>
        {error && <div className="sf2-empty" style={{ paddingTop: 10 }}>{error}</div>}
      </div>

      {nics.length === 0 && !error && (
        <div className="sf2-card">
          <div className="sf2-empty">
            Sin tarjetas de red inventariadas. Se rellenan desde el inventario de componentes o con el escaneo del agente.
          </div>
        </div>
      )}

      <div className="sf2-grid">
        {nics.map((nic) => {
        const puertos = Array.isArray(nic.ports) ? nic.ports : [];
        return (
          <div className="sf2-card" key={nic.id}>
            <h3>
              {nombreNic(nic)}
              <span className="sf2-count">{puertos.length} puerto{puertos.length === 1 ? '' : 's'}</span>
            </h3>
            <div className="sf2-rows">
              {puertos.length === 0 && <div className="sf2-empty">Sin puertos declarados.</div>}
              {puertos.map((p) => (
                <div className="sf2-row" key={p.id || `${nic.id}-${p.label || p.port_number}`}>
                  <div className="sf2-row-main">
                    <b>{p.label || `P${p.port_number}`}</b>
                    <small>
                      {[p.port_type, p.speed, p.mac_address ? `MAC ${p.mac_address}` : null]
                        .filter(Boolean).join(' · ') || 'Sin datos del puerto'}
                    </small>
                  </div>
                  <span className={`sf2-pill${p.link_up ? ' sf2-pill--ok' : ''}`}>
                    {p.link_up ? 'Enlace' : 'Libre'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {teams.length > 0 && (
        <div className="sf2-card">
          <h3>Equipos (teams)<span className="sf2-count">{teams.length}</span></h3>
          <div className="sf2-rows">
            {teams.map((team, index) => (
              <div className="sf2-row" key={team.name || `team-${index}`}>
                <div className="sf2-row-main">
                  <b>{team.name || `Team ${index + 1}`}</b>
                  <small>
                    {[
                      team.mode,
                      team.vlan_id ? `VLAN ${team.vlan_id}` : null,
                      team.ip_address ? `${team.ip_address}${team.prefix ? `/${team.prefix}` : ''}` : null,
                      Array.isArray(team.member_port_ids) ? `${team.member_port_ids.length} miembros` : null,
                    ].filter(Boolean).join(' · ') || 'Sin configuración'}
                  </small>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
